import {
  Component,
  ChangeDetectionStrategy,
  computed,
  inject,
} from '@angular/core';
import { DashboardCardComponent } from '../../../../shared/components/dashboard-card/dashboard-card.component';
import { ExploreCardComponent } from '../../../../shared/components/explore-card/explore-card.component';
import { Router } from '@angular/router';
import { EXTERNAL_URLS } from '../../../../shared/config/external-urls';
import { NotificationService } from '../../../../shared/services/notification.service';

interface DashboardCard {
  title: string;
  description: string;
  buttonText: string;
  icon: string;
  route: string;
}

interface ExploreCard {
  title: string;
  description: string;
  icon: string;
  link: string;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [DashboardCardComponent, ExploreCardComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent {
  private readonly router = inject(Router);
  readonly notificationService = inject(NotificationService);

  private static readonly MY_TRANSLATION_JOBS_DEFAULT_TEXT =
    'View and manage all your translation jobs in one place.';
  private static readonly QUOTES_TO_BE_REVIEWED_DEFAULT_TEXT =
    'Track pending vendor quotes and review them when ready.';

  dashboardCards: DashboardCard[] = [
    {
      title: 'Request a Vendor Quote',
      description: 'A quick link to the translation request form.',
      buttonText: 'Intake Form',
      icon: 'article_person',
      route: 'vendor-translation',
    },
    {
      title: 'My Translation Jobs',
      description: 'View and manage all your translation jobs in one place.',
      buttonText: 'Translation Manager',
      icon: 'library_books',
      route: '/translation-manager',
    },
    {
      title: 'Quotes to be Reviewed',
      description: 'The quote for 1 job is ready for your review.',
      buttonText: 'Quote Review',
      icon: 'receipt_long',
      route: '/translation-manager?prefilter=quotes',
    },
  ];

  exploreCards: ExploreCard[] = [
    {
      title: 'Plain Text Translation',
      description:
        'Use our AI powered translation tool for plain text. As easy as copy and pasting!',
      icon: 'translate',
      link: 'https://www.deepl.com/en/translator',
    },
    {
      title: 'Machine Translation',
      description:
        'Upload your documents and have them immediately translated with our AI Translation tool.',
      icon: 'desktop_windows',
      link: 'https://www.deepl.com/en/translator/files',
    },
  ];

  readonly cardNotificationDots = computed<Record<string, boolean>>(() => ({
    'My Translation Jobs':
      this.notificationService.hasCompletedDocsNotification() ||
      this.notificationService.hasNewMessagesNotification(),
    'Quotes to be Reviewed':
      this.notificationService.hasAwaitingVendorNotification(),
  }));

  getCardDescription(card: DashboardCard): string {
    if (card.title === 'My Translation Jobs') {
      return this.getMyTranslationJobsDescription();
    }

    if (card.title === 'Quotes to be Reviewed') {
      return this.getQuotesToBeReviewedDescription();
    }

    return card.description;
  }

  private getMyTranslationJobsDescription(): string {
    const completedJobsCount = this.notificationService.completedJobs().length;

    if (completedJobsCount === 0) {
      return HomeComponent.MY_TRANSLATION_JOBS_DEFAULT_TEXT;
    }

    const verb = completedJobsCount === 1 ? 'is' : 'are';
    const noun = completedJobsCount === 1 ? 'job' : 'jobs';

    return `There ${verb} ${completedJobsCount} recently completed translated ${noun} ready for your review.`;
  }

  private getQuotesToBeReviewedDescription(): string {
    const awaitingVendorCount =
      this.notificationService.awaitingVendorJobs().length;

    if (awaitingVendorCount === 0) {
      return HomeComponent.QUOTES_TO_BE_REVIEWED_DEFAULT_TEXT;
    }

    if (awaitingVendorCount === 1) {
      return 'The quote for 1 job is ready for your review.';
    }

    return `The quotes for ${awaitingVendorCount} jobs are ready for your review.`;
  }

  navigateTo(route: string): void {
    if (route === '/translation-manager?prefilter=quotes') {
      this.router.navigate(['/translation-manager'], {
        state: {
          prefilterQuotes: true,
          selectedFilters: ['Quotes Pending', 'Quotes to be Reviewed'],
        },
      });
      return;
    }

    if (route.startsWith('http')) {
      window.open(route, '_self');
    } else {
      this.router.navigate([route]);
    }
  }
}
